import {
  BottomSheetBackdrop,
  BottomSheetModal,
  BottomSheetView,
  type BottomSheetModalProps,
} from '@gorhom/bottom-sheet'
import React, {
  memo,
  useCallback,
  useLayoutEffect,
  useRef,
  type PropsWithChildren,
  type RefObject,
} from 'react'
import { Platform, StyleSheet, View } from 'react-native'
import { useSpinDelay } from 'spin-delay'
import { create } from 'zustand/react'
import { useShallow } from 'zustand/react/shallow'
import CloseButton from '~/components/CloseButton'
import { ScrollContextProvider } from '~/components/layout/ScrollContext'
import designTokens from '~/lib/designTokens'
import { cn } from '~/lib/utils'

type BottomSheetState = 'open' | 'closed' | 'open-overlay'

type BottomSheetStoreType = {
  bottomSheetStates: Map<string, BottomSheetState>
  setBottomSheetState: (key: string, state: BottomSheetState) => void
}

export const useBottomSheetStore = create<BottomSheetStoreType>((set) => ({
  bottomSheetStates: new Map<string, BottomSheetState>(),
  setBottomSheetState: (key, sheetState) =>
    set((state) => {
      const next = new Map(state.bottomSheetStates)
      next.set(key, sheetState)

      return { bottomSheetStates: next }
    }),
}))

export function useBottomSheetState(modalKey?: string) {
  const { bottomSheetStates } = useBottomSheetStore()

  if (!modalKey) {
    return 'closed'
  }

  return bottomSheetStates.get(modalKey) ?? 'closed'
}

export function useIsBottomSheetOpen(modalKey?: string) {
  const { bottomSheetStates: openBottomSheetKeys } = useBottomSheetStore()
  const isOpen = modalKey
    ? openBottomSheetKeys.has(modalKey) && openBottomSheetKeys.get(modalKey) !== 'closed'
    : openBottomSheetKeys.values().some((state) => state !== 'closed')

  return useSpinDelay(isOpen, {
    delay: 100,
    minDuration: 300,
  })
}

const BottomSheet = memo(function BottomSheet({
  children,
  modalKey,
  isOpen,
  onOpenChange,
  showHandle = true,
  className,
  showCloseButton = true,
  closeButtonClassName,
  ref: refProp,
  stackBehavior = 'switch',
  closeOnBackdropPress = true,
  isOverlay = false,
  snapPoints,
  manuallyCloseable = showHandle,
}: PropsWithChildren<{
  modalKey: string
  isOpen?: boolean
  onOpenChange?: (isOpen: boolean) => void
  showHandle?: boolean
  className?: string
  showCloseButton?: boolean
  closeButtonClassName?: string
  ref?: RefObject<BottomSheetModal | null>
  stackBehavior?: BottomSheetModalProps['stackBehavior']
  closeOnBackdropPress?: boolean
  isOverlay?: boolean
  snapPoints?: BottomSheetModalProps['snapPoints']
  manuallyCloseable?: boolean
}>) {
  const setBottomSheetState = useBottomSheetStore(useShallow((state) => state.setBottomSheetState))

  const internalRef = useRef<BottomSheetModal>(null)
  const ref = refProp ?? internalRef

  // eslint-disable-next-line i18next/no-literal-string
  const currentState = useRef<'open' | 'closed'>('closed')

  useLayoutEffect(() => {
    if (isOpen) {
      setBottomSheetState(modalKey, isOverlay ? 'open-overlay' : 'open')

      if (currentState.current === 'closed') {
        ref.current?.present()
        // eslint-disable-next-line i18next/no-literal-string
        currentState.current = 'open'
      }
    } else {
      setBottomSheetState(modalKey, 'closed')

      if (currentState.current !== 'closed') {
        ref.current?.dismiss()
        // eslint-disable-next-line i18next/no-literal-string
        currentState.current = 'closed'
      }
    }

    return () => {
      setBottomSheetState(modalKey, 'closed')
    }
  }, [isOpen, modalKey, isOverlay, setBottomSheetState, ref])

  const SheetBackdrop = useCallback(
    (props: React.ComponentProps<typeof BottomSheetBackdrop>) => (
      <BottomSheetBackdrop
        {...props}
        pressBehavior={closeOnBackdropPress ? 'close' : 'none'}
        appearsOnIndex={0}
        disappearsOnIndex={-1}
        style={[props.style, { cursor: 'auto' }]}
      />
    ),
    [closeOnBackdropPress]
  )

  return (
    <BottomSheetModal
      ref={ref}
      name={modalKey}
      enableDynamicSizing
      snapPoints={snapPoints}
      enableHandlePanningGesture={showHandle}
      enablePanDownToClose={showHandle && manuallyCloseable}
      enableContentPanningGesture={manuallyCloseable}
      handleComponent={showHandle ? undefined : null}
      backdropComponent={!isOverlay ? SheetBackdrop : undefined}
      enableDismissOnClose
      stackBehavior={stackBehavior}
      containerStyle={Platform.OS !== 'web' ? styles.container : undefined}
      backgroundStyle={{
        borderRadius: 0,
        borderTopLeftRadius: 38,
        borderTopRightRadius: 38,
      }}
      style={Platform.OS === 'web' ? [styles.container] : undefined}
      handleStyle={{
        backgroundColor: 'transparent',
      }}
      onDismiss={() => {
        if (onOpenChange) {
          onOpenChange(false)
        } else {
          setBottomSheetState(modalKey, 'closed')
          // eslint-disable-next-line i18next/no-literal-string
          currentState.current = 'closed'
        }
      }}>
      <ScrollContextProvider>
        <BottomSheetView className="relative rounded-none">
          <View className={cn('relative flex-1', className)}>
            {showCloseButton && (
              <CloseButton
                className={cn(
                  'absolute left-6 right-auto top-0 z-50 bg-background',
                  !showHandle && 'top-5',
                  closeButtonClassName
                )}
                onPress={() => {
                  onOpenChange?.(false)
                  return false
                }}
              />
            )}
            {children}
          </View>
        </BottomSheetView>
      </ScrollContextProvider>
    </BottomSheetModal>
  )
})

const styles = StyleSheet.create({
  container: {
    shadowColor: designTokens.primary,
    shadowOffset: { width: 0, height: -2 },
    shadowOpacity: 0.3,
    shadowRadius: 20,
    elevation: 5,
    borderTopLeftRadius: 38,
    borderTopRightRadius: 38,
    maxWidth: 504,
    marginHorizontal: 'auto',
  },
})

export default BottomSheet
